import { message } from "antd";
import axios from "axios";
import { isArray } from "lodash";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

// ----------------------------------------------------------------------

const axiosInstance = axios.create({ baseURL: BASE_URL });

axiosInstance.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error.response && error.response.data && error.response.data.errors) {
      isArray(error.response.data.errors) &&
        error.response.data.errors.forEach((value: any) =>
          message.error(value?.errorMsg),
        );
    }
    return Promise.reject();
  },
);

interface DownloadFileParams {
  url: string;
  autoDownload?: boolean;
  fileName?: string;
  mimeType?: string;
  fileNameWithMimeType?: string;
}

export const downloadFile = async ({
  url,
  autoDownload = true,
  fileName = "file.xlsx",
}: DownloadFileParams): Promise<string> => {
  try {
    const res = await axiosInstance.get(url, { responseType: "blob" });
    const newUrl = window.URL.createObjectURL(res.data);

    if (autoDownload && fileName) {
      const link = document.createElement("a");
      link.href = newUrl;
      link.setAttribute("download", fileName);
      link.click();
    }

    return newUrl;
  } catch (error: any) {
    return Promise.reject(error);
  }
};

export default axiosInstance;

export const endpoints = {
  department: { list: "staff/v1/bitrix-chat-bot/department-options" },
  employees: {
    list: (scheduleId: string) =>
      `/turniket/v1/bitrix/work-schedule/users/${scheduleId}`,
    advanceblacklist: (scheduleId: string) =>
      `turniket/v1/bitrix/employee/get-all-not-get-advance`,
  },
  schedule: {
    list: "/turniket/v1/bitrix/work-schedule",
    add: "/turniket/v1/bitrix/work-schedule",
    form: (query: string = "") =>
      `/turniket/v1/bitrix/work-schedule/form${query}`,
    one: (id: string) => `/turniket/v1/bitrix/work-schedule/${id}`,
    update: (id: string) => `/turniket/v1/bitrix/work-schedule/${id}`,
    delete: (id: string) => `/turniket/v1/bitrix/work-schedule/${id}`,
    moveToFolder: (folderId: string, scheduleId: string) =>
      `/turniket/v1/bitrix/work-schedule/move-to-folder?folderId=${folderId}&scheduleId=${scheduleId}`,
  },

  folder: {
    add: "/turniket/v1/bitrix/work-schedule-folder",
    delete: (id: string) => `/turniket/v1/bitrix/work-schedule-folder/${id}`,
    update: (id: string) => `/turniket/v1/bitrix/work-schedule-folder/${id}`,
  },
  manager: {
    list: "/turniket/v1/bitrix/work-schedule-manager",
    add: (userId: string) =>
      `/turniket/v1/bitrix/work-schedule-manager?userId=${userId}`,
    delete: (id: string) => `/turniket/v1/bitrix/work-schedule-manager/${id}`,
    search: (query: string = "") =>
      `/turniket/v1/bitrix/work-schedule-manager/search${query}`,
  },
  survey: {
    list: "/staff/v1/survey/get-all",
    add: "/staff/v1/survey/add",
    clone: "/staff/v1/survey/clone",
    publish: "/staff/v1/bitrix-chat-bot/send-notification",
    exel: (id: string) => `/staff/v1/survey/download-excel/${id}`,
    one: (id: string) => `/staff/v1/survey/get-one/${id}?withQuestions=true`,
    update: (id: string) => `/staff/v1/survey/edit/${id}`,
    delete: (id: string) => `/staff/v1/survey/delete/${id}`,
  },
  surveyQuestion: {
    list: "/staff/v1/question/get-all",
    add: "/staff/v1/question/add",
    changeOrderIndexes: "/staff/v1/question/change-order-index",
    one: (id: string) => `/staff/v1/question/get-one/${id}`,
    update: (id: string) => `/staff/v1/question/edit/${id}`,
    delete: (id: string) => `/staff/v1/question/delete/${id}`,
  },
  surveyQuestionAnswer: {
    list: "/staff/v1/answer/get-all",
    add: "/staff/v1/answer/add",
    one: (id: string) => `/staff/v1/answer/get-one/${id}`,
    update: (id: string) => `/staff/v1/answer/edit/${id}`,
    delete: (id: string) => `/staff/v1/answer/delete/${id}`,
  },
  taskReDeadline: {
    get: (taskId: string) => `/pdp-bot/v1/task-re-deadline/${taskId}`,
    create: `/pdp-bot/v1/task-re-deadline`,
  },
  md: {
    list: "/turniket/v1/bitrix/work-schedule-md",
    add: "/turniket/v1/bitrix/work-schedule-md",
    delete: "/turniket/v1/bitrix/work-schedule-md",
    search: (query: string = "") =>
      `/turniket/v1/bitrix/work-schedule-md/search${query}`,
    company: "/turniket/v1/bitrix/work-schedule-md/form",
  },
  employee: {
    list: "/turniket/v1/bitrix/employee/get-all-blocked-send-notification",
    add: (bxUserId: string) =>
      `/turniket/v1/bitrix/employee/is-send-notification/${bxUserId}`,
    delete: (bxUserId: string) =>
      `/turniket/v1/bitrix/employee/is-send-notification/${bxUserId}`,
    // 🔹 NEW ADVANCE BLACKLIST ENDPOINTS
    advanceBlacklistList:
      "/turniket/v1/bitrix/employee/get-all-excluded-from-advance",
    // "/turniket/v1/bitrix/employee/get-all-not-get-advance",

    advanceBlacklistAdd: (bxUserId: string) =>
      `/turniket/v1/bitrix/employee/toggle-advance-exclusion/${bxUserId}`,
    // `/turniket/v1/bitrix/employee/not-get-advance/${bxUserId}`,

    advanceBlacklistDelete: (bxUserId: string) =>
      `/turniket/v1/bitrix/employee/toggle-advance-exclusion/${bxUserId}`,
    // `/turniket/v1/bitrix/employee/not-get-advance/${bxUserId}`,
    search: (query: string = "") =>
      `/turniket/v1/bitrix/work-schedule-md/search${query}`,
    company: "/turniket/v1/bitrix/work-schedule-md/form",
  },
  explanationLetter: {
    // list: (year: number | string, month: number | string, departmentIds: number[] | [], day: number | null) => `/turniket/v1/bitrix/user-late-info?year=${year}&month=${month}&departmentIds=${departmentIds}&day=${day}`,
    list: (
      year: number | string,
      month: number | string,
      departmentIds: number[] | [],
      day?: number | null,
      companyIds?: number[],
    ) => {
      const params = new URLSearchParams();
      params.append("year", String(year));
      params.append("month", String(month));
      params.append("departmentIds", departmentIds.join(","));
      if (day !== null && day !== undefined) {
        params.append("day", String(day));
      }
      if (companyIds !== null && companyIds !== undefined) {
        params.append("companyIds", companyIds.join(","));
      }
      return `/turniket/v1/bitrix/user-late-info?${params.toString()}`;
    },
    changeStatus: "/turniket/v1/bitrix/user-late-info",
  },
  partner: {
    companies: "/turniket/v1/partner/company",
    company: "/turniket/v1/partner/company",
    companyDelete: (id: string) => `/turniket/v1/partner/company/${id}`,
    employee: "/turniket/v1/partner/employee",
    employeeDelete: (id: string) => `/turniket/v1/partner/employee/${id}`,
    uploadPhoto: "/turniket/v1/partner/upload-photo",
  },
  employeeAttendance: {
    list: (
      fromDate: string,
      toDate: string,
      companyIds: string[],
      departmentIds: string[],
    ) => {
      const params = new URLSearchParams();
      if (companyIds && companyIds.length > 0) {
        params.append("companyIds", companyIds.join(","));
      }
      if (departmentIds && departmentIds.length > 0) {
        params.append("departmentIds", departmentIds.join(","));
      }
      return `/turniket/v1/integration/bitrix/attendance-dashboard?fromDate=${fromDate}&toDate=${toDate}&${params.toString()}`;
    },
    filter: "/turniket/v1/integration/bitrix/attendance-dashboard/form",
  },
};
